import React, { useState, useEffect } from 'react';
import { Modal, Form, Input, Select, Radio, Upload, Button, message } from 'antd';
import { CloseOutlined, ClockCircleOutlined } from '@ant-design/icons';
import { useAuth } from '@clerk/clerk-react';
import styles from './SupportTicketModal.module.css';

const BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8000';
const { TextArea } = Input;
const { Option } = Select;

const ISSUE_TYPES = [
  { value: 'Market Search not loading', label: 'Market Search not loading' },
  { value: 'Inventory not loading', label: 'Inventory not loading' },
  { value: 'Bug', label: 'Bug Report' },
  { value: 'Feature Request', label: 'Feature Request' },
  { value: 'Question', label: 'Question' },
  { value: 'Billing Issue', label: 'Billing Issue' },
  { value: 'Other', label: 'Other' },
];

const PRIORITY_OPTIONS = [
  { value: 'low', label: 'Low — General question' },
  { value: 'medium', label: 'Medium — Feature not working' },
  { value: 'high', label: 'High — Blocking workflow' },
];

/** Map form issue type to API issue_type (snake_case). */
function toIssueTypeApi(value) {
  const map = {
    'Market Search not loading': 'market_search_not_loading',
    'Inventory not loading': 'inventory_not_loading',
    'Bug': 'bug',
    'Feature Request': 'feature_request',
    'Question': 'question',
    'Billing Issue': 'billing_issue',
    'Other': 'other',
  };
  return map[value] ?? value?.toLowerCase().replace(/\s+/g, '_') ?? 'other';
}

/** Map form priority (low/medium/high) to API (Low/Medium/High). */
function toPriorityApi(value) {
  if (!value) return 'Medium';
  const v = String(value).toLowerCase();
  return v === 'low' ? 'Low' : v === 'high' ? 'High' : 'Medium';
}

export default function SupportTicketModal({ open, onClose, prefill }) {
  const { getToken, user } = useAuth();
  const [form] = Form.useForm();
  const [fileList, setFileList] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const clerkEmail = user?.primaryEmailAddress?.emailAddress ?? user?.emailAddresses?.[0]?.emailAddress ?? '';

  useEffect(() => {
    if (open) {
      const values = { email: clerkEmail || undefined };
      if (prefill && typeof prefill === 'object') {
        if (prefill.issueType != null) values.issueType = prefill.issueType;
        if (prefill.priority != null) values.priority = prefill.priority;
        if (prefill.subject != null) values.subject = prefill.subject;
        if (prefill.description != null) values.description = prefill.description;
      }
      form.setFieldsValue(values);
    }
  }, [open, clerkEmail, form, prefill]);

  const handleSubmit = async (values) => {
    setSubmitting(true);
    try {
      const token = await getToken({ template: 'default' });
      const email = (values.email || clerkEmail || '').trim();
      if (!email || !email.includes('@')) {
        message.error('Please enter a valid email address.');
        setSubmitting(false);
        return;
      }

      const payload = {
        issue_type: toIssueTypeApi(values.issueType),
        subject: values.subject,
        description: values.description,
        priority: toPriorityApi(values.priority),
        screenshot_url: null,
        email,
      };

      const res = await fetch(`${BASE_URL}/support/submit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const errText = await res.text();
        throw new Error(errText || `Request failed: ${res.status}`);
      }

      message.success('Support ticket submitted. We\'ll get back to you shortly.');
      form.resetFields();
      setFileList([]);
      onClose?.();
    } catch (err) {
      console.error('Support ticket submit error:', err);
      message.error(err?.message || 'Failed to submit ticket. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      closable={false}
      footer={null}
      width={960}
      wrapClassName={styles.wrap}
      className={styles.modal}
      styles={{ body: { padding: 0 } }}
      getContainer={false}
      destroyOnClose
    >
      <div className={styles.inner}>
        <header className={styles.header}>
          <h2 id="support-ticket-title" className={styles.title}>
            Submit a Support Ticket
          </h2>
          <p className={styles.subtitle}>
            Tell us what&apos;s going on and we&apos;ll get back to you shortly.
          </p>
          <button
            type="button"
            className={styles.closeBtn}
            onClick={onClose}
            aria-label="Close"
          >
            <CloseOutlined />
          </button>
        </header>
        <div className={styles.divider} />
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{
            issueType: 'Market Search not loading',
            priority: 'medium',
            email: clerkEmail || undefined,
          }}
          className={styles.form}
        >
          <Form.Item
            name="email"
            label="Email"
            rules={[
              { required: true, message: 'Email is required' },
              { type: 'email', message: 'Enter a valid email address' },
            ]}
            className={styles.formItem}
          >
            <Input
              type="email"
              placeholder="you@example.com"
              className={styles.input}
            />
          </Form.Item>
          <div className={styles.row1}>
            <Form.Item
              name="issueType"
              label="Issue Type"
              rules={[{ required: true, message: 'Required' }]}
              className={styles.formItemIssueType}
            >
              <Select
                placeholder="Market Search not loading"
                className={styles.select}
                dropdownClassName={styles.dropdown}
                getPopupContainer={(n) => n?.parentElement ?? document.body}
              >
                {ISSUE_TYPES.map((opt) => (
                  <Option key={opt.value} value={opt.value}>
                    {opt.label}
                  </Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item
              name="priority"
              label="Priority"
              className={styles.formItemPriority}
            >
              <Radio.Group options={PRIORITY_OPTIONS} className={styles.radioGroup} />
            </Form.Item>
          </div>

          <Form.Item
            name="subject"
            label="Subject"
            rules={[{ required: true, message: 'Required' }]}
            className={styles.formItem}
          >
            <Input
              placeholder="e.g. Market Search stuck on loading…"
              className={styles.input}
            />
          </Form.Item>

          <Form.Item
            name="description"
            label="Description"
            rules={[{ required: true, message: 'Required' }]}
            className={styles.formItem}
          >
            <TextArea
              rows={3}
              placeholder="Describe what's happening. Include vehicle details, VIN, or screenshots if relevant."
              className={styles.textarea}
            />
          </Form.Item>

          <Form.Item name="screenshot" className={styles.formItemUpload}>
            <Upload.Dragger
              fileList={fileList}
              onChange={({ fileList: next }) => setFileList(next)}
              beforeUpload={() => false}
              accept="image/*"
              maxCount={1}
              className={styles.dragger}
            >
              <p className={styles.uploadTitle}>Upload screenshot (optional)</p>
              <p className={styles.uploadHint}>Drag or click to attach image files</p>
            </Upload.Dragger>
          </Form.Item>

          <div className={styles.actions}>
            <Button
              type="primary"
              htmlType="submit"
              loading={submitting}
              className={styles.submitBtn}
            >
              Submit Ticket
            </Button>
            <p className={styles.footerNote}>
              <ClockCircleOutlined className={styles.footerIcon} />
              <span>Average response time: under 2 hours</span>
            </p>
          </div>
        </Form>
      </div>
    </Modal>
  );
}
